/**
 * supabase.ts
 * Single Supabase client for the app, created from environment variables.
 *
 * If the env vars are absent (no `.env`, or a fresh checkout), the client is
 * `null` and the whole data layer falls back to bundled defaults + localStorage.
 * This keeps the game fully playable offline and before the DB is provisioned.
 *
 * Only the ANON (public) key is used here — never the service_role key.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** True when both Supabase env vars are present. */
export const isSupabaseConfigured = Boolean(url && anonKey);

/** Raw credentials for keepalive fetch calls (safe — these are the public anon key). */
export const supabaseUrl   = isSupabaseConfigured ? (url as string)     : null;
export const supabaseAnon  = isSupabaseConfigured ? (anonKey as string) : null;

/** The shared client, or null when Supabase is not configured. */
function makeClient(): SupabaseClient | null {
  if (!isSupabaseConfigured) return null;
  try {
    return createClient(url as string, anonKey as string, {
      auth: { persistSession: true, autoRefreshToken: true },
    });
  } catch (e) {
    console.error('[supabase] createClient failed — running without cloud features:', e);
    return null;
  }
}
export const supabase: SupabaseClient | null = makeClient();

if (!isSupabaseConfigured && import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.info(
    '[data] Supabase not configured — using bundled defaults + localStorage. ' +
      'Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to .env to enable cloud features.',
  );
}
